import { ExternalLink } from 'lucide-react'
import { useContext, useState } from 'react'
import { toast } from 'sonner'

import { CopyButtonWithAnimationActionIcon } from 'components/reusable/CopyButtonWithAnimation'
import DisplayTooltipOnHover from 'components/reusable/DisplayTooltipOnHover'
import { adminRecordInspector } from 'rails_routes'
import { Badge } from 'slab'
import { useGetBrokersUnprocessedEnrollmentsId } from 'utils/web_api'

import BrokersEnrollmentStatusOutput from './BrokersEnrollmentStatusOutput'
import BrokersEnrollmentWorkflows from './BrokersEnrollmentWorkflows'
import Coverages from './Coverages'
import EnrollmentPaymentMethod from './EnrollmentPaymentMethod'
import { type MoneyAmount } from '../../types'
import {
  getFormattedDate,
  getLocaleCurrencyStringFromMoneyAmount,
} from '../../utils'
import BrokersMemberContext from '../context/BrokersMemberContext'
import { Banner } from '../reusable/Banner'
import { SkeletonItem } from '../reusable/SkeletonItem'

type BrokersEnrollmentProps = {
  memberId: string
  enrollmentId: string
  editCoveragesPath: string
  memberConfirmedDateTime?: string
  submissionProcessingDateTime?: string
  embedded?: boolean
}

const GenericErrorBanner = ({
  actionType,
}: {
  actionType: 'loading' | 'updating'
}) => {
  return (
    <Banner className='mb-4' titleText='Something went wrong' type='critical'>
      <p>
        An error occurred while {actionType} the enrollment. Please contact
        support for assistance.
      </p>
    </Banner>
  )
}

export const BrokersEnrollmentSkeleton = () => {
  return (
    <div className='border border-slate-200 rounded-lg p-4 mb-4'>
      <div className='animate-pulse'>
        <div>
          <div className='flex justify-between items-start mb-2'>
            <div className='flex items-center gap-2'>
              <SkeletonItem heightClass='h-8' widthClass='w-32' />
              <SkeletonItem heightClass='h-6' widthClass='w-16' />
            </div>
            <SkeletonItem heightClass='h-6' widthClass='w-16' />
          </div>

          {/* Status section skeleton */}
          <div className='flex justify-start items-center space-x-2 flex-wrap mb-8'>
            <SkeletonItem heightClass='h-6' widthClass='w-36' />
            <SkeletonItem heightClass='h-6' widthClass='w-32' />
            <SkeletonItem heightClass='h-6' widthClass='w-40' />
          </div>
        </div>

        {/* Coverages section skeleton */}
        <div className='mt-4space-y-2'>
          <SkeletonItem heightClass='h-4' widthClass='w-32' />
          <div className='mt-1'>
            <SkeletonItem heightClass='h-16' widthClass='w-full' />
          </div>
        </div>

        {/* Payment method section skeleton */}
        <div className='mt-8 space-y-2'>
          <SkeletonItem heightClass='h-4' widthClass='w-40' />
          <div className='border border-gray-200 rounded-lg p-1'>
            <SkeletonItem heightClass='h-8' widthClass='w-full' />
          </div>
        </div>
      </div>
    </div>
  )
}

export function BrokersEnrollment ({
  memberId,
  enrollmentId,
  editCoveragesPath,
  embedded = false,
}: BrokersEnrollmentProps) {
  const { isUserThatchAdmin } = useContext(BrokersMemberContext)
  const [actionError, setActionError] = useState<boolean>(false)
  const {
    data: enrollment,
    isLoading,
    isError,
  } = useGetBrokersUnprocessedEnrollmentsId(enrollmentId)

  const handleActionError = () => {
    setActionError(true)
  }

  if (isLoading && !enrollment) {
    return <BrokersEnrollmentSkeleton />
  }

  if (!isLoading && isError) {
    return <GenericErrorBanner actionType='loading' />
  }

  if (!enrollment) {
    return <div>No enrollment information found</div>
  }

  const {
    id,
    coverage,
    carrier_member_id: carrierMemberId,
    display_premium: displayPremium,
    hios_id: hiosId,
    member_confirmed_at: memberConfirmedAt,
    plan_code: planCode,
    title,
    sbc_url: sbcUrl,
    submission_processing_at: submissionProcessingAt,
  } = enrollment
  return (
    <div className={embedded ? '' : 'border border-slate-200 rounded-lg p-4 mb-4'}>
      {actionError && <GenericErrorBanner actionType='updating' />}
      <div>
        <div className='flex justify-between items-start mb-2'>
          <div className='flex items-center gap-2'>
            <h2 className='h2 font-bold'>
              {coverage.charAt(0).toUpperCase() + coverage.slice(1)}
            </h2>
            <a href={adminRecordInspector.show({ id })}>
              <Badge type='info' leftIcon={ExternalLink}>
                {id.slice(-6)}
              </Badge>
            </a>
          </div>
          <h2 className='h2 font-bold text-right'>
            {' '}
            {getLocaleCurrencyStringFromMoneyAmount(displayPremium as MoneyAmount)}
            /mo
          </h2>
        </div>

        <div className='flex justify-between items-center mb-2'>
          <div>{title}</div>
          {carrierMemberId && (
            <DisplayTooltipOnHover text='Carrier Member ID'>
              <Badge
                bordered
                type='info'
                actionIcon={CopyButtonWithAnimationActionIcon}
                actionLabel='Copy Carrier Member ID'
                onActionClick={async () => {
                  try {
                    await navigator.clipboard.writeText(carrierMemberId || ''
                    )
                    toast.success('Carrier Member ID copied to clipboard', {
                      position: 'top-center',
                    })
                  } catch (error) {
                    console.error('Failed to copy to clipboard:', error)
                    toast.error('Failed to copy to clipboard', {
                      position: 'top-center',
                    })
                  }
                }}
              >
                <span className='ph-no-capture'>
                  {carrierMemberId}
                </span>
              </Badge>
            </DisplayTooltipOnHover>
          )}
        </div>

        <BrokersEnrollmentStatusOutput
          handleActionError={handleActionError}
          enrollmentId={enrollmentId}
          memberId={memberId}
        />

        <div className='mt-8'>
          {hiosId && (
            <div className='text-sm'>
              <span className='font-semibold'>HIOS ID:</span>{' '}
              {hiosId}{' '}
              {sbcUrl && (<a href={sbcUrl}>(SBC)</a>)}
              {isUserThatchAdmin && planCode && (
                <a href={adminRecordInspector.show({ id: planCode })}>
                  (View Ideon data in admin)
                </a>)}
            </div>
          )}

          <div className='text-sm'>
            <span className='font-semibold'>Confirmed:</span>{' '}
            {memberConfirmedAt
              ? getFormattedDate(memberConfirmedAt, {
                hour: 'numeric',
                minute: '2-digit',
              })
              : 'N/A'}
          </div>

          <div className='text-sm'>
            <span className='font-semibold'>Submitted:</span>{' '}
            {submissionProcessingAt
              ? getFormattedDate(submissionProcessingAt, {
                hour: 'numeric',
                minute: '2-digit',
              })
              : 'N/A'}
          </div>
        </div>
      </div>

      <div className='mb-4 space-x-4 flex' />
      <Coverages
        enrollmentId={enrollmentId}
        memberId={memberId}
        isEditEnrollmentV2Enabled={isUserThatchAdmin || false}
        editCoveragesPath={editCoveragesPath}
      />

      <EnrollmentPaymentMethod memberId={memberId} enrollment={enrollment} />

      {!embedded && <BrokersEnrollmentWorkflows enrollmentId={enrollmentId} />}
    </div>
  )
}

export default BrokersEnrollment
